import React, { useState, useEffect, useCallback } from 'react';
import {
    Syringe,
    Search,
    User,
    Clock,
    AlertCircle,
    CheckCircle,
    FileText,
    RefreshCw,
    Wifi
} from 'lucide-react';
import './NursingPanel.css';

import { API_URL } from '../config/api';
const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

const NursingPanel = ({ embedded = false }) => {
    const [pendingSamples, setPendingSamples] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showOnlyUrgent, setShowOnlyUrgent] = useState(false);
    const [selectedStudy, setSelectedStudy] = useState(null);
    const [showCollectModal, setShowCollectModal] = useState(false);
    const [collectData, setCollectData] = useState({
        sample_type: 'Sangre venosa',
        collected_at: '',
        notes: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const fetchPendingSamples = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            setIsRefreshing(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/nursing/pending-samples`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setPendingSamples(data);
                setLastUpdated(new Date());
            }
        } catch (error) {
            console.error('Error fetching pending samples:', error);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, []);

    // Initial fetch and auto-refresh
    useEffect(() => {
        fetchPendingSamples();

        const interval = setInterval(() => {
            fetchPendingSamples(false);
        }, AUTO_REFRESH_INTERVAL);

        return () => clearInterval(interval);
    }, [fetchPendingSamples]);

    const handleCollectSample = async (e) => {
        e.preventDefault();
        if (!selectedStudy) return;

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/patient-studies/${selectedStudy.id}/sample`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(collectData)
            });

            if (res.ok) {
                setShowCollectModal(false);
                setSelectedStudy(null);
                setCollectData({
                    sample_type: 'Sangre venosa',
                    collected_at: '',
                    notes: ''
                });
                fetchPendingSamples();
            } else {
                const error = await res.json();
                alert(error.message || 'Error al registrar muestra');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error de conexión');
        } finally {
            setSubmitting(false);
        }
    };

    const openCollectModal = (study) => {
        setSelectedStudy(study);
        // Usar hora local del sistema (compensando offset UTC)
        const now = new Date();
        const localISOTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        setCollectData({
            sample_type: 'Sangre venosa',
            collected_at: localISOTime,
            notes: ''
        });
        setShowCollectModal(true);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const filteredSamples = pendingSamples.filter(sample => {
        const matchesSearch =
            `${sample.patient_first_name} ${sample.patient_last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
            sample.study_name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesUrgent = !showOnlyUrgent || sample.priority === 'urgente';
        return matchesSearch && matchesUrgent;
    });

    const urgentCount = pendingSamples.filter(s => s.priority === 'urgente').length;

    return (
        <div className={`nursing-panel ${embedded ? 'embedded' : ''}`}>
            {!embedded && (
                <div className="panel-header">
                    <div className="header-title">
                        <div className="header-icon">
                            <Syringe size={28} />
                        </div>
                        <div>
                            <h1>Panel de Enfermería</h1>
                            <p>Toma de Muestras</p>
                        </div>
                    </div>
                    <div className="header-actions">
                        <div className="live-indicator">
                            <Wifi size={14} className={isRefreshing ? 'pulse' : ''} />
                            <span>En vivo</span>
                            {lastUpdated && (
                                <span className="last-updated">
                                    · Actualizado {lastUpdated.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            )}
                        </div>
                        <button className={`btn-refresh ${isRefreshing ? 'refreshing' : ''}`} onClick={() => fetchPendingSamples()}>
                            <RefreshCw size={18} className={isRefreshing ? 'spin' : ''} />
                            Actualizar
                        </button>
                    </div>
                </div>
            )}

            <div className="panel-stats">
                <div className="stat-card">
                    <span className="stat-value">{pendingSamples.length}</span>
                    <span className="stat-label">Pendientes</span>
                </div>
                <div className="stat-card urgent">
                    <span className="stat-value">{urgentCount}</span>
                    <span className="stat-label">Urgentes</span>
                </div>
            </div>

            <div className="panel-filters">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Buscar paciente o estudio..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <label className="checkbox-filter">
                    <input
                        type="checkbox"
                        checked={showOnlyUrgent}
                        onChange={(e) => setShowOnlyUrgent(e.target.checked)}
                    />
                    Solo urgentes
                </label>
            </div>

            <div className="samples-list">
                {loading ? (
                    <div className="loading-state">Cargando muestras pendientes...</div>
                ) : filteredSamples.length === 0 ? (
                    <div className="empty-state">
                        <CheckCircle size={48} strokeWidth={1} />
                        <p>No hay muestras pendientes</p>
                    </div>
                ) : (
                    filteredSamples.map(sample => (
                        <div
                            key={sample.id}
                            className={`sample-card ${sample.priority === 'urgente' ? 'urgent' : ''}`}
                        >
                            <div className="sample-priority">
                                {sample.priority === 'urgente' ? (
                                    <span className="priority-badge urgent">🔴 URGENTE</span>
                                ) : (
                                    <span className="priority-badge">⏳ Rutina</span>
                                )}
                            </div>

                            <div className="patient-info">
                                <User size={18} />
                                <div>
                                    <span className="patient-name">
                                        {sample.patient_first_name} {sample.patient_last_name}
                                    </span>
                                    <span className="patient-id">Exp: {sample.patient_code || `#${sample.patient_id}`}</span>
                                </div>
                            </div>

                            <div className="study-info">
                                <FileText size={16} />
                                <span>{sample.study_name}</span>
                            </div>

                            <div className="request-info">
                                <span><strong>Médico:</strong> {sample.doctor_name}</span>
                                <span><Clock size={14} /> {formatDate(sample.requested_at)}</span>
                            </div>

                            {sample.patient_instructions && (
                                <div className="instructions">
                                    <AlertCircle size={14} />
                                    <span>{sample.patient_instructions}</span>
                                </div>
                            )}

                            {sample.special_notes && (
                                <div className="special-notes">
                                    <AlertCircle size={14} />
                                    <span><strong>Nota:</strong> {sample.special_notes}</span>
                                </div>
                            )}

                            <button
                                className="btn-collect"
                                onClick={() => openCollectModal(sample)}
                            >
                                <Syringe size={16} />
                                Registrar Muestra
                            </button>
                        </div>
                    ))
                )}
            </div>

            {/* Collect Sample Modal */}
            {showCollectModal && selectedStudy && (
                <div className="modal-overlay" onClick={() => setShowCollectModal(false)}>
                    <div className="modal-content collect-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Registrar Toma de Muestra</h2>
                            <button className="close-btn" onClick={() => setShowCollectModal(false)}>×</button>
                        </div>

                        <form onSubmit={handleCollectSample}>
                            <div className="modal-body">
                                <div className="patient-summary">
                                    <p><strong>Paciente:</strong> {selectedStudy.patient_first_name} {selectedStudy.patient_last_name}</p>
                                    <p><strong>Estudio:</strong> {selectedStudy.study_name}</p>
                                </div>

                                <div className="form-group">
                                    <label>Tipo de muestra</label>
                                    <select
                                        value={collectData.sample_type}
                                        onChange={(e) => setCollectData(prev => ({ ...prev, sample_type: e.target.value }))}
                                    >
                                        <option value="Sangre venosa">Sangre venosa</option>
                                        <option value="Sangre capilar">Sangre capilar</option>
                                        <option value="Orina">Orina</option>
                                        <option value="Heces">Heces</option>
                                        <option value="Saliva">Saliva</option>
                                        <option value="Otro">Otro</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Fecha y hora de toma</label>
                                    <input
                                        type="datetime-local"
                                        value={collectData.collected_at}
                                        onChange={(e) => setCollectData(prev => ({ ...prev, collected_at: e.target.value }))}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Observaciones (opcional)</label>
                                    <textarea
                                        value={collectData.notes}
                                        onChange={(e) => setCollectData(prev => ({ ...prev, notes: e.target.value }))}
                                        placeholder="Ej: Toma sin complicaciones..."
                                        rows={3}
                                    />
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setShowCollectModal(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-submit" disabled={submitting}>
                                    {submitting ? 'Registrando...' : 'Registrar Muestra'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NursingPanel;
